import {useMemo, useState} from 'react';

import {ArrowDownward} from '@mui/icons-material';
import {
  Grid,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';
import {EmptyStateCard} from '../EmptyStateCard/EmptyStateCard';
import {LoadingContainer} from '../Loading/LoadingContainer';

import {greenColors, orangeColors, semantic} from '../../layouts/theme/designSystem';

export interface TableColumn<RowData extends TableRowRequired = TableRowRequired> {
  id: string;
  label: string;
  minWidth?: string;
  maxWidth?: string;
  align?: 'right' | 'left' | 'center';
  sx?: SxProps<Theme>;
  sortable?: boolean;
  getSortValue?: (row: RowData) => number | string | null;
}

export interface TableRowRequired {
  id: string;
}

export type TableSortDirection = 'asc' | 'desc';

export interface TableSortConfig {
  columnId: string | null;
  direction: TableSortDirection;
}

interface Props<RowData extends TableRowRequired, Col extends TableColumn<RowData>> {
  columns: Col[];
  Row: (props: RowData) => React.ReactNode;
  rowData: RowData[];
  hasMoreRows?: boolean;
  fetchNext?: () => void;
  isLoading?: boolean;
  buttonText?: string;
  headerTextColor?: string;
  hideLoadMoreFooter?: boolean;
  /** Stretch table to 100% of parent; use with column `sx` widths for Figma-style full-bleed headers. */
  fullWidthLayout?: boolean;
  sortingMode?: 'memory' | 'server';
  onSortChange?: (sort: TableSortConfig) => void;
  initialSortConfig?: TableSortConfig;
  rootSx?: SxProps<Theme>;
  tableContainerSx?: SxProps<Theme>;
  tableSx?: SxProps<Theme>;
  headerRowSx?: SxProps<Theme>;
  headerCellSx?: SxProps<Theme>;
}

export const BasicTable = <RowData extends TableRowRequired, Col extends TableColumn<RowData> = TableColumn<RowData>>({
  columns,
  rowData,
  Row,
  isLoading,
  fetchNext,
  hasMoreRows,
  buttonText,
  headerTextColor,
  hideLoadMoreFooter,
  fullWidthLayout = true,
  sortingMode = 'memory',
  onSortChange,
  initialSortConfig,
  rootSx,
  tableContainerSx,
  tableSx,
  headerRowSx,
  headerCellSx,
}: Props<RowData, Col>) => {
  const [sortConfig, setSortConfig] = useState<TableSortConfig>(
    initialSortConfig || {
      columnId: null,
      direction: 'asc',
    }
  );

  const sortedRowData = useMemo(() => {
    if (sortingMode !== 'memory') {
      return rowData;
    }

    if (!sortConfig.columnId) {
      return rowData;
    }

    const column = columns.find((col) => col.id === sortConfig.columnId) as
      | (Col & {
          sortable?: boolean;
          getSortValue?: (row: RowData) => number | string | null;
        })
      | undefined;

    if (!column || !column.sortable || !column.getSortValue) {
      return rowData;
    }

    const dataCopy = [...rowData];

    dataCopy.sort((a, b) => {
      const aVal = column.getSortValue ? column.getSortValue(a) : null;
      const bVal = column.getSortValue ? column.getSortValue(b) : null;

      if (aVal == null && bVal == null) {
        return 0;
      }

      if (aVal == null) {
        return 1;
      }

      if (bVal == null) {
        return -1;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLocaleLowerCase();
      const bStr = String(bVal).toLocaleLowerCase();

      if (aStr < bStr) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }

      if (aStr > bStr) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }

      return 0;
    });

    return dataCopy;
  }, [columns, rowData, sortConfig, sortingMode]);

  const handleSort = (column: Col) => {
    const sortableColumn = column as Col & {sortable?: boolean};

    if (!sortableColumn.sortable) {
      return;
    }

    setSortConfig((prev) => {
      const prevConfig = {
        columnId: column.id,
        direction: prev.direction === 'asc' ? 'desc' : ('asc' as TableSortDirection),
      };
      const config = {
        columnId: column.id,
        direction: 'asc' as TableSortDirection,
      };
      const nextConfig: TableSortConfig = prev.columnId === column.id ? prevConfig : config;

      if (onSortChange) {
        onSortChange(nextConfig);
      }

      return nextConfig;
    });
  };

  const resolvedHeaderColor = headerTextColor ?? greenColors[700];
  const showLoadMoreFooter = !hideLoadMoreFooter && !!fetchNext && !!buttonText;
  const withSx = (...styles: (SxProps<Theme> | undefined)[]): SxProps<Theme> =>
    styles.filter((style): style is SxProps<Theme> => style !== undefined) as SxProps<Theme>;

  return (
    <Grid
      container
      flexDirection='column'
      gap={showLoadMoreFooter ? '32px' : 0}
      alignItems={fullWidthLayout ? 'stretch' : 'center'}
      sx={withSx(fullWidthLayout ? {width: '100%'} : undefined, rootSx)}
    >
      <TableContainer
        sx={withSx(
          fullWidthLayout ? {width: '100%', maxWidth: '100%', overflowX: 'hidden'} : undefined,
          {border: 'none', boxShadow: 'none'},
          tableContainerSx
        )}
      >
        <Table
          sx={withSx(
            fullWidthLayout ? {width: '100%', tableLayout: 'fixed'} : {width: '100%'},
            {
              borderCollapse: 'separate',
              borderSpacing: 0,
              border: 'none',
            },
            tableSx
          )}
        >
          <TableHead>
            <TableRow sx={withSx({height: '40px'}, headerRowSx)}>
              {columns.map((column) => {
                const sortableColumn = column as Col & {sortable?: boolean};
                const isSortable = !!sortableColumn.sortable;
                const isActiveSortColumn = isSortable && sortConfig.columnId === column.id;

                return (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    onClick={isSortable ? () => handleSort(column) : undefined}
                    style={{
                      top: 57,
                      ...(column.minWidth && {minWidth: column.minWidth}),
                      ...(column.maxWidth && {maxWidth: column.maxWidth}),
                    }}
                    sx={withSx(
                      {
                        bgcolor: semantic.warning.bg,
                        borderBottom: `1px solid ${orangeColors[3]}`,
                        py: '9px',
                        px: '14px',
                        ...(isSortable && {cursor: 'pointer'}),
                      },
                      headerCellSx,
                      column.sx
                    )}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        color: resolvedHeaderColor,
                        fontWeight: 600,
                        fontSize: '15px',
                        lineHeight: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {column.label}
                      {isSortable && (
                        <ArrowDownward
                          fontSize='inherit'
                          sx={{
                            opacity: isActiveSortColumn ? 1 : 0.4,
                            transform: isActiveSortColumn && sortConfig.direction === 'asc' ? 'rotate(180deg)' : 'none',
                            fontSize: '1.125rem',
                          }}
                        />
                      )}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRowData.map((row) => (
              <Row key={row.id} {...row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!isLoading && !rowData.length && <EmptyStateCard />}
      {showLoadMoreFooter && (
        <EmpionButton
          onClick={fetchNext}
          disabled={isLoading || !hasMoreRows}
          disabledColor='transparent'
          hoverTextColor={greenColors[70]}
          hoverBackgroundColor='transparent'
          textColor={greenColors[900]}
          sx={{backgroundColor: 'transparent', fontWeight: 400}}
        >
          <Grid container justifyContent='center' gap='8px'>
            {isLoading ? (
              <LoadingContainer minHeight='100%' />
            ) : (
              <>
                {hasMoreRows && <ArrowDownward sx={{alignSelf: 'center'}} />}
                {hasMoreRows ? buttonText : ''}
              </>
            )}
          </Grid>
        </EmpionButton>
      )}
    </Grid>
  );
};
